/*
  Card behaviour for snippets/eme-product-card.liquid and snippets/eme-pdp-card.liquid:
  the wishlist bookmark (with EME's size step) and the media carousel.

  EME's wishlist is a real logged-in customer feature backed by their own service
  (wishlist-service.client-B_YDLihC.js). That is not portable to a Liquid theme, so this keeps
  the exact affordance (bookmark -> bookmarkFilled, aria-pressed, active:scale-90) and stores the
  handles in localStorage.
  ponytail: localStorage, swap for a customer metafield when accounts are wired up.

  One delegated listener for the whole document, so card count does not matter.

  The snippet emits this <script src> once per rendered card unless load_css:false is passed, and
  duplicate <script src> elements DO each execute (the HTML spec does not dedupe by URL). Two
  listeners meant every click toggled the id in and straight back out, so the wishlist silently
  saved nothing on any page with an even number of copies. Hence the guard below: it must stay.
*/
(function () {
  if (window.__emeWishlistBound) return;
  window.__emeWishlistBound = true;

  var KEY = 'eme:wishlist';
  /* The chosen size, kept BESIDE the list rather than inside it. sections/eme-wishlist-drawer.liquid
     reads the list as bare handles and fetches /products/<handle>.js from each one, so the array
     shape cannot change without breaking it. */
  var VKEY = 'eme:wishlist:variant';

  function read() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(v) ? v : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (err) {
      /* private mode: the button still toggles for this paint, it just will not persist */
    }
    // One event, so the header count, the bookmark icon and the drawer all follow the same
    // source of truth instead of each polling localStorage on their own schedule.
    document.dispatchEvent(new CustomEvent('eme:wishlist:changed', { detail: { list: list } }));
  }

  function readVariants() {
    try {
      var v = JSON.parse(localStorage.getItem(VKEY));
      return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
    } catch (e) {
      return {};
    }
  }

  function writeVariants(map) {
    try {
      localStorage.setItem(VKEY, JSON.stringify(map));
    } catch (err) {
      /* same as write(): the pick still applies to this page view */
    }
  }

  function paint() {
    var ids = read();
    var btns = document.querySelectorAll('[data-eme-wish]');
    for (var i = 0; i < btns.length; i++) {
      var on = ids.indexOf(btns[i].getAttribute('data-eme-wish')) > -1;
      btns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = btns[i].getAttribute(on ? 'data-label-on' : 'data-label-off');
      if (label) btns[i].setAttribute('aria-label', label);
    }
  }

  /* Cards are emitted with aria-pressed="false" hardcoded, so any grid that arrives after load
     (the collection's infinite scroll appends `next.children` straight into the grid) renders
     every saved product as unsaved, and the next click on it REMOVES the item. Whoever swaps DOM
     in calls this. EME cannot fail this way: their `B` derives the state from the store on every
     render. */
  window.__emeWishPaint = function () {
    paint();
    bindTracks(document);
  };

  // Exposed so the drawer can remove a row and so anything else can read the list without
  // duplicating the storage key or the migration below.
  window.emeWishlist = {
    key: KEY,
    read: read,
    write: function (list) { write(list); paint(); },
    remove: function (handle) { removeHandle(handle); },
    /* The size the shopper picked when saving, or null. Nothing reads this yet: the drawer
       resolves each row to the first available variant, which is right for a single-variant
       catalogue and wrong the day sizes exist.
       ponytail: one line in the drawer's render() (`variantFor(handle) || first available`)
       adopts it, but that file is out of scope here. */
    variantFor: function (handle) {
      var v = readVariants()[handle];
      return v ? Number(v) : null;
    },
  };

  function removeHandle(handle) {
    var list = read();
    var at = list.indexOf(handle);
    if (at === -1) return;
    list.splice(at, 1);
    var map = readVariants();
    delete map[handle];
    writeVariants(map);
    write(list);
    paint();
  }

  function saveHandle(handle, variantId) {
    var list = read();
    if (list.indexOf(handle) === -1) list.push(handle);
    if (variantId) {
      var map = readVariants();
      map[handle] = Number(variantId);
      writeVariants(map);
    }
    write(list);
    paint();
    // `addProductToWishlist: t => e({ isOpen: !0, ... })`: saving always opens the drawer.
    document.dispatchEvent(new CustomEvent('eme:wishlist:open'));
  }

  /* ------------------------------------------------------------------ *
   * Size step
   *
   * EME's wishlist store (price-range-display-jE3ccH2T.js `nn`) never writes on the card click:
   *   addProductToWishlist: t => e({ isOpen: !0, productToAdd: t, state: `selecting` })
   *   confirmVariantSelection: () => e({ state: `pendingAdd` })
   * and layout-BMy3aoz6.js `pi` renders that `selecting` branch inside the drawer as a
   * neutral #F4F4F4 banner reading selectSize, the product row (image 95px, sm 120px, aspectRatio 1,
   * gap 8px) and a horizontally scrolling strip of 32x32 buttons with a transparent bottom
   * border that turns black on hover and on the selection. Removing is NOT gated: their `B`
   * calls removeFromWishlist straight from the card, which is what removeHandle does.
   *
   * The panel is mounted INTO sections/eme-wishlist-drawer.liquid rather than rebuilt as a
   * second sheet, so there is one drawer on screen exactly as on EME. That section is off
   * limits here, hence the DOM-side mount and the styles below instead of an edit to it.
   * ------------------------------------------------------------------ */
  var pickPanel = null;
  var pickHost = null;
  var pickWatcher = null;

  function ensurePickStyles() {
    if (document.getElementById('eme-wishpick-css')) return;
    var el = document.createElement('style');
    el.id = 'eme-wishpick-css';
    /* Not in assets/eme-product-card.css because the PDP renders cards (and so this step)
       without ever loading that stylesheet. One <style>, injected once, on first use. */
    el.textContent = [
      '.eme-wishpick{position:absolute;inset:0;z-index:20;display:flex;flex-direction:column;',
      'gap:12px;overflow-y:auto;padding:10px;background:#fff;color:#000}',
      '.eme-wishpick__banner{display:flex;flex-shrink:0;flex-direction:column;align-items:center;',
      'justify-content:center;gap:12px;border-radius:4px;background:#F4F4F4;padding:12px}',
      '.eme-wishpick p{margin:0;font-size:11px;line-height:15px;letter-spacing:-0.22px;',
      'text-transform:uppercase}',
      '.eme-wishpick__row{display:flex;gap:8px}',
      '.eme-wishpick__thumb{flex:0 0 95px;width:95px;aspect-ratio:1;overflow:hidden;',
      'border-radius:4px;background:#F5F5F5}',
      '@media (min-width:640px){.eme-wishpick__thumb{flex-basis:120px;width:120px}}',
      '.eme-wishpick__thumb img{display:block;width:100%;height:100%;object-fit:cover}',
      '.eme-wishpick__meta{display:flex;min-width:0;flex-direction:column;gap:4px}',
      '.eme-wishpick__sizes{display:flex;overflow-x:auto;scrollbar-width:none}',
      '.eme-wishpick__sizes::-webkit-scrollbar{display:none}',
      /* `pi`'s button is `shrink-0 cursor-pointer rounded-[0.4rem] disabled:pointer-events-none
         disabled:opacity-40` and its <p> is `label mx-auto flex h-[3.2rem] w-[3.2rem] ...
         border-transparent border-b hover:border-black`. That mx-auto is a no-op inside a
         shrink-wrapped button; putting it on the flex ITEM instead made `auto` eat the strip's
         free space and spread three sizes across the full width, where EME packs them left. */
      '.eme-wishpick__size{display:flex;height:32px;width:32px;flex:0 0 32px;align-items:center;',
      'justify-content:center;border:0;border-bottom:1px solid transparent;border-radius:4px;',
      'background:none;color:inherit;cursor:pointer;transition:border-color .15s}',
      '.eme-wishpick__size:hover:not([disabled]){border-bottom-color:#000}',
      '.eme-wishpick__size[disabled]{pointer-events:none;opacity:.4}',
    ].join('');
    document.head.appendChild(el);
  }

  function closePick() {
    if (pickPanel && pickPanel.parentNode) pickPanel.parentNode.removeChild(pickPanel);
    if (pickHost) pickHost.style.position = '';
    pickPanel = null;
    pickHost = null;
  }

  function cardOf(btn) {
    return (btn.closest && btn.closest('.eme-card')) || document.body;
  }

  function text(root, sel) {
    var el = root.querySelector(sel);
    return el ? el.textContent.trim() : '';
  }

  function openPick(btn, handle, variants) {
    var drawer = document.querySelector('[data-eme-wishlist]');
    var host = drawer && drawer.querySelector('.eme-wish__card');
    // No drawer on this template: save straight away rather than swallow the click.
    if (!host) {
      saveHandle(handle, firstAvailable(variants));
      return;
    }

    closePick();
    ensurePickStyles();
    document.dispatchEvent(new CustomEvent('eme:wishlist:open'));

    var card = cardOf(btn);
    var img = card.querySelector('img');
    var panel = document.createElement('div');
    panel.className = 'eme-wishpick';
    panel.setAttribute('role', 'group');
    // Hardcoded English for the same reason as the bookmark's aria-label: the locale files have
    // no wishlist keys and inventing one fails theme-check.
    panel.setAttribute('aria-label', 'Select size');

    var head = document.createElement('div');
    head.className = 'eme-wishpick__banner';
    head.innerHTML = '<p>Select size</p>';

    var row = document.createElement('div');
    row.className = 'eme-wishpick__row';
    var thumb = document.createElement('div');
    thumb.className = 'eme-wishpick__thumb';
    if (img) {
      var shot = document.createElement('img');
      shot.src = img.currentSrc || img.src;
      shot.alt = '';
      thumb.appendChild(shot);
    }
    var meta = document.createElement('div');
    meta.className = 'eme-wishpick__meta';
    var name = document.createElement('p');
    name.textContent = text(card, '.eme-card__title') || btn.getAttribute('data-eme-wish');
    var price = document.createElement('p');
    price.textContent = text(card, '.eme-card__price');
    meta.appendChild(name);
    meta.appendChild(price);
    row.appendChild(thumb);
    row.appendChild(meta);

    var strip = document.createElement('div');
    strip.className = 'eme-wishpick__sizes';
    variants.forEach(function (v) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'eme-wishpick__size';
      b.setAttribute('data-vid', v.id);
      if (v.available === false) b.disabled = true;
      b.innerHTML = '<p></p>';
      b.firstChild.textContent = v.title;
      strip.appendChild(b);
    });

    panel.appendChild(head);
    panel.appendChild(row);
    panel.appendChild(strip);

    panel.addEventListener('click', function (e) {
      var size = e.target.closest && e.target.closest('[data-vid]');
      if (!size) return;
      e.preventDefault();
      closePick();
      saveHandle(handle, size.getAttribute('data-vid'));
    });

    host.style.position = 'relative';
    host.appendChild(panel);
    pickPanel = panel;
    pickHost = host;
    var first = panel.querySelector('.eme-wishpick__size:not([disabled])');
    if (first) first.focus();

    // Closing the drawer abandons the step, same as EME's sheet unmounting the branch.
    if (!pickWatcher) {
      pickWatcher = new MutationObserver(function () {
        if (drawer.getAttribute('data-state') !== 'open') closePick();
      });
      pickWatcher.observe(drawer, { attributes: true, attributeFilter: ['data-state'] });
    }
  }

  function firstAvailable(variants) {
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available !== false) return variants[i].id;
    }
    return variants.length ? variants[0].id : null;
  }

  function variantsOf(btn) {
    try {
      var raw = btn.getAttribute('data-eme-variants');
      var v = raw ? JSON.parse(raw) : [];
      return Array.isArray(v) ? v : [];
    } catch (e) {
      return [];
    }
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-eme-wish]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var handle = btn.getAttribute('data-eme-wish');
    if (read().indexOf(handle) > -1) {
      removeHandle(handle);
      return;
    }
    var variants = variantsOf(btn);
    // One size is not a choice. EME's catalogue always has sizes, so their `selecting` state is
    // never a one-button strip; here it would be a pointless second click on every product.
    if (variants.length < 2) {
      saveHandle(handle, firstAvailable(variants));
      return;
    }
    openPick(btn, handle, variants);
  });

  /* ------------------------------------------------------------------ *
   * Media carousel
   *
   * product-card-DNWclM9u.js `mt` feeds the shared Embla wrapper `ft` with
   *   options: { dragFree:!1, loop:!!i, startIndex:x, watchDrag: f===`mobile`||f===`tablet` },
   *   showArrows:!0
   * The track itself is a native scroll-snap container (assets/eme-product-card.css), so touch
   * and trackpad need no JS at all. This adds the three things scroll-snap cannot do on its own:
   * the desktop arrows, the hover jump, and the garment/on-model switch.
   *
   * Deliberately NOT registered in assets/eme-dragscroll.js: `watchDrag` is mobile/tablet only,
   * i.e. EME turns mouse drag OFF for card media and gives the desktop arrows instead.
   * ------------------------------------------------------------------ */
  function slides(track) {
    return track.children.length;
  }

  function goTo(track, i, smooth) {
    var w = track.clientWidth || 1;
    var max = slides(track) - 1;
    if (i < 0) i = max;
    if (i > max) i = 0;
    track.scrollTo({ left: i * w, behavior: smooth ? 'smooth' : 'auto' });
  }

  /* `pt(view, {productIndex, modelIndex})`: product view rests on 0, model view on 1. */
  function baseIndex(track) {
    var scope = track.closest('[data-card-view]');
    return scope && scope.getAttribute('data-card-view') === 'model' && slides(track) > 1 ? 1 : 0;
  }

  /* `mt`'s hover effect scrolls to the OTHER shot: model view hovers back to the garment, and
     product view hovers to media.hover.index. */
  function hoverIndex(track) {
    var want = track.getAttribute('data-eme-hover');
    if (want === null) return null;
    if (baseIndex(track) === 1) return 0;
    var i = Number(want);
    return isNaN(i) ? null : Math.min(Math.max(i, 0), slides(track) - 1);
  }

  function bindTracks(root) {
    var tracks = (root || document).querySelectorAll('[data-eme-card-track]');
    for (var i = 0; i < tracks.length; i++) {
      (function (track) {
        if (track.dataset.emeCardBound === 'true') return;
        track.dataset.emeCardBound = 'true';
        var media = track.parentElement;
        if (!media) return;
        // iOS synthesizes mouse events after a touch. Only a real hovering mouse
        // may change the preview; scrolling/tapping a card must never do so.
        media.addEventListener('pointerenter', function (event) {
          if (event.pointerType !== 'mouse' || !window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)').matches) return;
          var h = hoverIndex(track);
          if (h !== null) goTo(track, h, false);
        });
        media.addEventListener('pointerleave', function (event) {
          if (event.pointerType !== 'mouse') return;
          goTo(track, baseIndex(track), false);
        });
        if (baseIndex(track)) goTo(track, baseIndex(track), false);
      })(tracks[i]);
    }
  }

  document.addEventListener('click', function (e) {
    var arrow = e.target && e.target.closest && e.target.closest('[data-eme-card-arrow]');
    if (!arrow) return;
    e.preventDefault();
    var media = arrow.parentElement;
    var track = media && media.querySelector('[data-eme-card-track]');
    if (!track) return;
    var w = track.clientWidth || 1;
    // loop:true in every carousel layout in the payload, so both ends wrap.
    goTo(track, Math.round(track.scrollLeft / w) + (Number(arrow.getAttribute('data-dir')) || 1), true);
  });

  /* The collection toolbar publishes the garment/on-model choice as [data-card-view] on the grid
     (sections/eme-main-collection.liquid initCardView). For the hover-swap card that is a pure
     CSS opacity swap; a carousel has to be scrolled. */
  new MutationObserver(function (records) {
    records.forEach(function (r) {
      var tracks = r.target.querySelectorAll('[data-eme-card-track]');
      for (var i = 0; i < tracks.length; i++) goTo(tracks[i], baseIndex(tracks[i]), false);
    });
  }).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['data-card-view'],
  });

  /* Same problem as __emeWishPaint, solved from this side so it cannot be forgotten by a caller:
     the collection's infinite scroll does `this.grid.append(...next.children)`, and those cards
     carry the hardcoded aria-pressed="false" the snippet emits, so every saved product on page 2
     rendered as unsaved and the next click on it REMOVED the item. Appended cards also need
     their carousel bound. childList only, so painting (attributes) cannot re-trigger this. */
  var repaintQueued = false;
  var gridObserver = new MutationObserver(function () {
    if (repaintQueued) return;
    repaintQueued = true;
    requestAnimationFrame(function () {
      repaintQueued = false;
      paint();
      bindTracks(document);
    });
  });

  function watchGrids() {
    // The PDP's Recommended rail and predictive-search results are fetched and written as
    // innerHTML, so their cards arrive after boot in the same way collection page 2 does.
    var grids = document.querySelectorAll('[data-eme-grid], [data-eme-reco-grid], [data-search-grid]');
    for (var i = 0; i < grids.length; i++) gridObserver.observe(grids[i], { childList: true });
  }

  function boot() {
    // The store used to hold numeric product ids. The drawer needs handles so it can fetch
    // each saved product back, so anything that is not a handle is dropped once, silently.
    var list = read();
    var clean = list.filter(function (v) { return typeof v === 'string' && !/^\d+$/.test(v); });
    if (clean.length !== list.length) write(clean);
    paint();
    bindTracks(document);
    watchGrids();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('eme:wishlist:changed', paint);
  // Sections re-render on theme-editor edits and on paginated/filtered grid swaps.
  document.addEventListener('shopify:section:load', function () {
    paint();
    bindTracks(document);
    watchGrids();
  });
  // A second tab saving something used to leave this one showing the stale bookmark, and the
  // next click there removed what the other tab had just added. `storage` only fires in the
  // OTHER tabs, so this cannot loop.
  window.addEventListener('storage', paint);
})();
